
import { useState, useEffect, useRef } from 'react';

export default function CompanyTimeline() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [counters, setCounters] = useState({ projects: 0, countries: 0, satisfaction: 0 });
  const sectionRef = useRef<HTMLElement>(null);

  const milestones = [
    {
      year: '2020',
      title: 'Foundation',
      description:
        'Äsili Visuals was established in Kampala, Uganda, with a vision to transform brand communication across East Africa.',
      icon: 'ri-rocket-2-line',
      color: '#038C7F',
      highlight: 'Kampala HQ Established',
      image:
        'https://readdy.ai/api/search-image?query=Modern%20startup%20office%20launch%20celebration%20in%20Kampala%20Uganda%20creative%20agency%20founding%20moment%20warm%20golden%20lighting%20professional%20workspace%20brand%20new%20office%20contemporary%20African%20design%20elements%20premium%20photography&width=600&height=400&seq=timeline-2020-v3&orientation=landscape',
    },
    {
      year: '2021',
      title: 'Regional Expansion',
      description:
        'Extended services to Kenya and Tanzania, delivering integrated campaigns for leading brands in hospitality and logistics.',
      icon: 'ri-map-pin-line',
      color: '#F2780C',
      highlight: 'Kenya & Tanzania Markets',
      image:
        'https://readdy.ai/api/search-image?query=East%20African%20business%20expansion%20map%20showing%20Kenya%20Tanzania%20Uganda%20connections%20modern%20corporate%20presentation%20professional%20atmosphere%20warm%20tones%20premium%20photography%20creative%20agency%20growth&width=600&height=400&seq=timeline-2021-v3&orientation=landscape',
    },
    {
      year: '2022',
      title: '100+ Projects Milestone',
      description:
        'Surpassed 100 successful projects across branding, outdoor advertising, film production, and digital marketing.',
      icon: 'ri-trophy-line',
      color: '#038C7F',
      highlight: 'Century of Success',
      image:
        'https://readdy.ai/api/search-image?query=Creative%20agency%20team%20celebrating%20milestone%20achievement%20modern%20office%20confetti%20professional%20celebration%20diverse%20African%20team%20warm%20lighting%20premium%20photography%20corporate%20success%20moment&width=600&height=400&seq=timeline-2022-v3&orientation=landscape',
    },
    {
      year: '2023',
      title: 'Full East African Coverage',
      description:
        'Expanded operations to Rwanda and South Sudan, becoming a truly pan-East African creative agency.',
      icon: 'ri-earth-line',
      color: '#F2780C',
      highlight: 'Rwanda & South Sudan',
      image:
        'https://readdy.ai/api/search-image?query=Pan%20African%20business%20meeting%20with%20representatives%20from%20multiple%20East%20African%20countries%20professional%20conference%20room%20modern%20setting%20diverse%20team%20collaboration%20warm%20lighting%20premium%20photography&width=600&height=400&seq=timeline-2023-v3&orientation=landscape',
    },
    {
      year: '4​024',
      title: 'Industry Recognition',
      description:
        'Recognized as a leading integrated advertising firm with 150+ projects delivered and partnerships across 5 countries.',
      icon: 'ri-award-line',
      color: '#038C7F',
      highlight: '150+ Projects Delivered',
      image:
        'https://readdy.ai/api/search-image?query=Award%20winning%20creative%20advertising%20agency%20showcase%20with%20trophies%20certificates%20modern%20display%20professional%20achievement%20recognition%20warm%20golden%20lighting%20premium%20photography%20corporate%20excellence&width=600&height=400&seq=timeline-2024-v3&orientation=landscape',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          const duration = 2000;
          const steps = 60;
          const interval = duration / steps;
          let step = 0;
          const timer = setInterval(() => {
            step++;
            const p = step / steps;
            const ease = 1 - Math.pow(1 - p, 3);
            setCounters({
              projects: Math.round(150 * ease),
              countries: Math.round(5 * ease),
              satisfaction: Math.round(98 * ease),
            });
            if (step >= steps) clearInterval(timer);
          }, interval);
        }
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-16 sm:py-20 lg:py-28 bg-[#0D0D0D] overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#038C7F]/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#F2780C]/30 to-transparent"></div>
        <div className="absolute top-20 left-10 w-48 h-48 border border-[#038C7F]/10 rounded-full hidden lg:block"></div>
        <div className="absolute bottom-20 right-10 w-36 h-36 border border-[#F2780C]/10 rotate-45 hidden lg:block"></div>
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div
          className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full mb-4">
            <i className="ri-time-line text-[#038C7F] text-sm"></i>
            <span className="text-[10px] sm:text-xs font-bold text-white/70 uppercase tracking-[0.2em]">
              Our Journey
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3">
            Growth{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
              Timeline
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-400 max-w-xl mx-auto">
            From a local startup to a regional powerhouse — here&apos;s how we&apos;ve grown
            across East Africa
          </p>
        </div>

        {/* Timeline Navigation - Horizontal */}
        <div
          className={`mb-10 sm:mb-14 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-white/10 hidden sm:block">
              <div
                className="h-full bg-gradient-to-r from-[#038C7F] to-[#F2780C] rounded-full transition-all duration-700"
                style={{
                  width: `${((activeIndex + 1) / milestones.length) * 100}%`,
                }}
              ></div>
            </div>

            <div className="flex justify-between items-start relative">
              {milestones.map((m, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`group flex flex-col items-center gap-2 cursor-pointer transition-all duration-500 flex-1 ${
                    idx <= activeIndex ? 'opacity-100' : 'opacity-40 hover:opacity-70'
                  }`}
                >
                  <div
                    className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                      idx === activeIndex
                        ? 'scale-110 shadow-xl'
                        : idx < activeIndex
                        ? 'scale-100'
                        : 'scale-90'
                    }`}
                    style={{
                      backgroundColor:
                        idx === activeIndex
                          ? m.color
                          : idx < activeIndex
                          ? `${m.color}40`
                          : 'rgba(255,255,255,0.05)',
                      boxShadow:
                        idx === activeIndex ? `0 0 20px ${m.color}40` : 'none',
                    }}
                  >
                    {idx === activeIndex && (
                      <div
                        className="absolute inset-0 rounded-full animate-ping opacity-20"
                        style={{ backgroundColor: m.color }}
                      ></div>
                    )}
                    <i
                      className={`${m.icon} text-lg ${
                        idx <= activeIndex ? 'text-white' : 'text-white/30'
                      }`}
                    ></i>
                  </div>
                  <span
                    className={`text-sm sm:text-base font-black transition-colors ${
                      idx === activeIndex ? 'text-white' : 'text-gray-500'
                    }`}
                  >
                    {m.year}
                  </span>
                  <span
                    className={`text-[9px] sm:text-[10px] font-medium uppercase tracking-wider text-center hidden sm:block transition-colors ${
                      idx === activeIndex ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {m.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active Milestone Detail */}
        <div
          className={`transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Left – Image */}
              <div className="relative overflow-hidden" style={{ minHeight: '300px' }}>
                <img
                  key={`timeline-img-${activeIndex}`}
                  src={milestones[activeIndex].image}
                  alt={milestones[activeIndex].title}
                  className="absolute inset-0 w-full h-full object-cover object-top animate-timelineFadeIn"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0D0D0D]/60 hidden lg:block"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/80 via-transparent to-transparent lg:hidden"></div>
                {/* Year overlay */}
                <div className="absolute top-5 left-5">
                  <div
                    className="px-4 py-2 rounded-xl"
                    style={{ backgroundColor: milestones[activeIndex].color }}
                  >
                    <span className="text-2xl font-black text-white">
                      {milestones[activeIndex].year}
                    </span>
                  </div>
                </div>
                {/* Highlight badge */}
                <div className="absolute bottom-5 left-5">
                  <div className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                    <span className="text-xs font-bold text-white">
                      {milestones[activeIndex].highlight}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right – Content */}
              <div
                className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center"
                key={`timeline-content-${activeIndex}`}
              >
                <div className="animate-timelineFadeIn">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${milestones[activeIndex].color}20` }}
                    >
                      <i
                        className={`${milestones[activeIndex].icon} text-xl`}
                        style={{ color: milestones[activeIndex].color }}
                      ></i>
                    </div>
                    <div>
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: milestones[activeIndex].color }}
                      >
                        Year {milestones[activeIndex].year}
                      </span>
                      <h3 className="text-xl sm:text-2xl lg:text-3xl font-black text-white">
                        {milestones[activeIndex].title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-6">
                    {milestones[activeIndex].description}
                  </p>

                  {/* Navigation arrows */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setActiveIndex(Math.max(0, activeIndex - 1))}
                      disabled={activeIndex === 0}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 hover:border-white/30 text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-xl"></i>
                    </button>
                    <button
                      onClick={() =>
                        setActiveIndex(Math.min(milestones.length - 1, activeIndex + 1))
                      }
                      disabled={activeIndex === milestones.length - 1}
                      className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-xl"></i>
                    </button>
                    <div className="flex items-center gap-1.5 ml-3">
                      {milestones.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveIndex(idx)}
                          className={`transition-all duration-300 rounded-full cursor-pointer ${
                            idx === activeIndex ? 'w-6 h-2' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                          }`}
                          style={{
                            backgroundColor:
                              idx === activeIndex ? milestones[activeIndex].color : undefined,
                          }}
                        ></button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div
          className={`mt-10 sm:mt-14 transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative bg-gradient-to-r from-[#038C7F] via-[#038C7F]/90 to-[#026b61] rounded-2xl p-6 sm:p-8 lg:p-10 overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#F2780C]/10 rounded-full blur-2xl"></div>
            <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-white/20 rounded-tl-xl hidden sm:block"></div>
            <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2 border-white/20 rounded-br-xl hidden sm:block"></div>

            <div className="relative">
              <h3 className="text-center text-lg sm:text-xl font-bold text-white mb-6 sm:mb-8">
                Where We Stand Today
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                {[
                  { value: `${counters.projects}+`, label: 'Projects Completed', icon: 'ri-folder-chart-line' },
                  { value: `${counters.countries}`, label: 'Countries Served', icon: 'ri-earth-line' },
                  { value: `${counters.satisfaction}%`, label: 'Client Satisfaction', icon: 'ri-heart-pulse-line' },
                  { value: '360°', label: 'Creative Solutions', icon: 'ri-refresh-line' },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center group">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-white/20 transition-colors">
                      <i className={`${stat.icon} text-lg text-white`}></i>
                    </div>
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-1">
                      {stat.value}
                    </div>
                    <div className="text-[10px] sm:text-xs text-white/70 uppercase tracking-wider font-medium">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes timelineFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-timelineFadeIn {
          animation: timelineFadeIn 0.5s ease-out;
        }
      `}</style>
    </section>
  );
}
